import { useEffect, useRef, useState } from 'react';

function sanitize(raw: string): string {
  let out = '';
  let seenDot = false;
  for (const ch of raw) {
    if (ch >= '0' && ch <= '9') out += ch;
    else if (ch === '.' && !seenDot) { out += '.'; seenDot = true; }
  }
  return out;
}

function format(raw: string): string {
  const [intPart = '', decPart] = raw.split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
}

function caretAfterNNonComma(formatted: string, n: number): number {
  if (n <= 0) return 0;
  let count = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (formatted[i] !== ',') {
      count++;
      if (count === n) return i + 1;
    }
  }
  return formatted.length;
}

interface Props {
  value: string; // plain digits (+ optional single '.') -- the same shape the parent already parseFloat()s
  onChange: (raw: string) => void;
  id?: string;
  placeholder?: string;
  style?: React.CSSProperties;
}

/** A number input that live-formats with thousands commas as you type (e.g. typing 2500000 shows
 * "2,500,000"), while handing the parent the same plain-digit string it always has -- no backend or
 * submit-parsing change needed. Native <input type="number"> can't display commas at all, so this
 * renders as text with a numeric-friendly keyboard (inputMode) instead. */
export default function CommaNumberInput({ value, onChange, id, placeholder, style }: Props) {
  const ref = useRef<HTMLInputElement>(null);
  const [display, setDisplay] = useState(format(sanitize(value || '')));

  useEffect(() => {
    setDisplay(format(sanitize(value || '')));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const caretPos = input.selectionStart ?? input.value.length;
    const nonCommaBeforeCaret = input.value.slice(0, caretPos).replace(/,/g, '').length;
    const raw = sanitize(input.value);
    const formatted = format(raw);
    setDisplay(formatted);
    onChange(raw);
    requestAnimationFrame(() => {
      if (ref.current) {
        const pos = caretAfterNNonComma(formatted, nonCommaBeforeCaret);
        ref.current.setSelectionRange(pos, pos);
      }
    });
  };

  return (
    <input
      ref={ref}
      id={id}
      type="text"
      inputMode="decimal"
      value={display}
      placeholder={placeholder}
      style={style}
      onChange={handleChange}
    />
  );
}
